import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useCoachGlance } from "../../pages/Admin/Dashboard/useCoachGlance";
import { selectExpectedPace } from "./expectedPace";
import {
  ALL_PERIODS,
  periodOptions,
  readStoredPeriod,
  squadMatchesPeriod,
  writeStoredPeriod,
} from "./periods";
import { readNamesConcealed, writeNamesConcealed } from "./privacy";
import { lifecycleForLevel } from "./reviewLifecycle";
import { useReviewLifecycle } from "./useReviewLifecycle";

/**
 * THE FACILITATOR OS SHELL CONTEXT.
 *
 * ONE GLANCE INSTANCE FOR THE WHOLE SHELL, and that is the reason this provider
 * exists rather than each surface calling the hook. `useCoachGlance` holds
 * per-invocation state AND owns its own interval and AbortController; calling it
 * from the shell and again from Coach would create two independent polls against
 * the same endpoint, at the same cadence, whose responses could disagree by one
 * tick. The strip would then state a freshness the grid below it did not share.
 * So the hook is invoked exactly once, here, and every consumer reads the same
 * envelope, the same status and the same refetch through context.
 *
 * MOUNTED ONCE, in Layout's admin branch, OUTSIDE <Routes>. Navigating between
 * the four surfaces therefore does not remount it and does not restart the poll.
 *
 * IT NEVER CALLS `/teacher-onboarding/dashboard`. That endpoint is a GET that
 * mutates: it provisions a teacher row, auto-joins a cohort and a challenge,
 * writes level and step, and 403s admins. A shell that fired it on mount would
 * make merely opening the app a write. The glance is the only shell data source,
 * and it is a pure read.
 */

const FacilitatorContext = createContext(null);

export const useFacilitatorContext = () => useContext(FacilitatorContext);

export const FacilitatorContextProvider = ({
  children,
  privacyUserId = null,
  facilitatorRoleId = null,
}) => {
  const privacyIdentity = String(privacyUserId ?? "").trim() || null;
  // The requested context. Null/null means "let the server pick its documented
  // default", which is what a facilitator with one cohort always gets.
  const [requested, setRequested] = useState({
    cohortId: null,
    challengeId: null,
  });

  const { data, status, error, refetch, refreshSnapshot } =
    useCoachGlance(requested);

  // The RESOLVED room, which is what identifies "which room am I looking at".
  // The requested context starts null and stays null for a single-cohort
  // facilitator, so keying anything on it would key it on nothing.
  const resolvedCohortId = data?.cohort?.id ?? null;
  const resolvedChallengeId = data?.challenge?.id ?? null;

  const contexts = useMemo(
    () => (Array.isArray(data?.contexts) ? data.contexts : []),
    [data],
  );

  /*
  |----------------------------------------------------------------------------
  | Onboarding lock
  |----------------------------------------------------------------------------
  | THE SHELL READS THE SAME SIGNAL COACH ALREADY ROUTES ITS PHASE ON, and it
  | reads it from the same envelope, so the nav and the panel underneath it can
  | never disagree about whether setup is finished. Coach's `phase` is
  | `data.onboarding_required ? "onboarding" : "level"`; this is that expression
  | with the surface-specific half removed.
  |
  | EXPLICIT `true` ONLY, and the direction is deliberate. Absent, null and a
  | still-loading envelope all mean "not known yet", and locking the nav of a
  | facilitator who finished setup weeks ago on every slow poll is a worse
  | failure than one unlocked frame before the first glance lands. Once a glance
  | HAS landed it owns the answer: a failed refresh keeps the last envelope, so
  | the lock does not flicker off on a network hiccup either.
  |
  | IT NEVER CALLS `/teacher-onboarding/dashboard`. Same rule as the provider's
  | own docblock: that endpoint is a GET that mutates, and a lock that had to
  | ask it whether to engage would make merely opening the app a write.
  */
  const onboardingRequired = data?.onboarding_required === true;

  /*
  |----------------------------------------------------------------------------
  | The room's level
  |----------------------------------------------------------------------------
  | ONE LEVEL PER ROOM, HELD IN ONE PLACE. Prepare used to own a `levelNumber`
  | of its own that started at 1 and was seeded from nothing, so a room whose
  | teacher setting was level 2 opened Prepare on level 1 and the command band
  | directly above it said 2. Two numbers, one room, no control that explained
  | the difference.
  |
  | SEEDED AND RE-SEEDED FROM THE ROOM'S OWN SETTING, through
  | `selectExpectedPace` and never off a raw field. That selector is the single
  | point every surface reads pace through precisely so the coming
  | (cohort, challenge)-scoped migration is a one-point change, and a second
  | reader of `expected_pace` here would have cost that.
  |
  | `resolvedLevel`, NOT `storedLevel`. A stored level past the end of the
  | challenge is clamped, and seeding Prepare with a level the challenge does
  | not have would open it on a notice rather than on a week.
  |
  | THE SELECTION SURVIVES POLLING AND FOLLOWS A REAL CHANGE. The effect's deps
  | are the room identity and the pace level, so a poll returning the same pace
  | re-runs nothing and a facilitator reading another level keeps reading it;
  | a teacher moving the pace re-seeds, because at that point the room IS on a
  | different level.
  |
  | THE MULTI-LEVEL-SQUADS GAP IS LEFT OPEN ON PURPOSE. This is one scalar per
  | room because pace is one scalar per room today. When squads can sit on
  | different levels, this becomes a per-squad lookup and the consumers change
  | with it; nothing here assumes a scalar is the final shape, and nothing here
  | pretends to solve it.
  */
  const paceLevel = selectExpectedPace(data)?.resolvedLevel ?? null;
  const roomKey = `${resolvedCohortId ?? "none"}:${resolvedChallengeId ?? "none"}`;
  const [levelSelection, setLevelSelection] = useState({
    roomKey: null,
    level: null,
  });

  useEffect(() => {
    setLevelSelection({
      roomKey: `${resolvedCohortId ?? "none"}:${resolvedChallengeId ?? "none"}`,
      level: paceLevel,
    });
  }, [resolvedCohortId, resolvedChallengeId, paceLevel]);

  // Fall back to the room's own setting during the frame between a room change
  // and the effect that re-seeds. Publishing the previous room's selection for
  // even one render would hand Prepare a level from a challenge it has left.
  const roomLevel =
    levelSelection.roomKey === roomKey ? levelSelection.level : paceLevel;

  const setRoomLevel = useCallback(
    (level) => {
      const next = Number(level);
      if (!Number.isInteger(next) || next < 1) return;
      setLevelSelection({ roomKey, level: next });
    },
    [roomKey],
  );

  /*
  |----------------------------------------------------------------------------
  | Period selection
  |----------------------------------------------------------------------------
  */

  const [period, setPeriodState] = useState(ALL_PERIODS);

  const options = useMemo(() => periodOptions(data?.squads), [data]);

  // IDENTITY RESET. The provider stays mounted across a cohort switch, so the
  // previous room's period would otherwise survive into a room where it means
  // something else or nothing at all. The stored value is re-read here rather
  // than in a mount-only initializer for the same reason: on first paint the
  // resolved room is not known yet.
  useEffect(() => {
    setPeriodState(
      readStoredPeriod(resolvedCohortId, resolvedChallengeId) ?? ALL_PERIODS,
    );
  }, [resolvedCohortId, resolvedChallengeId]);

  // AVAILABILITY GUARD. A period can disappear between polls (a roster edit, a
  // squad reassignment). Filtering by a group nothing matches would render an
  // empty room, which reads as "everyone left" rather than "that filter is
  // gone". Only evaluated on a ready response: dropping the selection while the
  // envelope is loading or errored would discard it on every network hiccup.
  useEffect(() => {
    if (period === ALL_PERIODS || status !== "ready") return;
    if (options.some((option) => option.key === period)) return;
    setPeriodState(ALL_PERIODS);
    writeStoredPeriod(ALL_PERIODS, resolvedCohortId, resolvedChallengeId);
  }, [period, options, status, resolvedCohortId, resolvedChallengeId]);

  const setPeriod = useCallback(
    (key) => {
      const next = key || ALL_PERIODS;
      setPeriodState(next);
      writeStoredPeriod(next, resolvedCohortId, resolvedChallengeId);
    },
    [resolvedCohortId, resolvedChallengeId],
  );

  const filterSquads = useCallback(
    (squads) =>
      (Array.isArray(squads) ? squads : []).filter((squad) =>
        squadMatchesPeriod(squad?.sections, period),
      ),
    [period],
  );

  /*
  |----------------------------------------------------------------------------
  | Shared-machine privacy
  |----------------------------------------------------------------------------
  */

  const [privacyState, setPrivacyState] = useState({
    identity: null,
    concealed: true,
    epoch: 0,
  });

  useEffect(() => {
    setPrivacyState((current) => ({
      identity: privacyIdentity,
      concealed: readNamesConcealed(privacyIdentity),
      epoch: current.epoch + 1,
    }));
  }, [privacyIdentity]);

  // Fail concealed during an authenticated-identity transition. Effects run
  // after paint, so exposing the prior identity's `false` for even one render
  // would let a newly signed-in facilitator inherit names-visible briefly.
  const namesConcealed =
    privacyState.identity === privacyIdentity
      ? privacyState.concealed !== false
      : true;
  // The identity is included directly, so an auth transition invalidates every
  // per-entry reveal during render, before the identity-loading effect runs.
  // The counter changes with each explicit privacy action. Consumers key any
  // visit-local reveal against this value and therefore cannot paint a name
  // from the previous privacy state for one frame.
  const privacyEpoch = `${privacyIdentity ?? "anonymous"}:${privacyState.epoch}`;

  const setNamesConcealed = useCallback(
    (concealed) => {
      const next = concealed !== false;
      setPrivacyState((current) => ({
        identity: privacyIdentity,
        concealed: next,
        epoch: current.epoch + 1,
      }));
      writeNamesConcealed(privacyIdentity, next);
    },
    [privacyIdentity],
  );

  /*
  |----------------------------------------------------------------------------
  | Review badge
  |----------------------------------------------------------------------------
  | SCOPE LIMITATION, NAMED RATHER THAN PAPERED OVER. The deliverables aggregate
  | takes no cohort or challenge parameter: it resolves its program from the
  | actor's most recent cohort membership
  | (TeacherOnboardingController.php:3948-3958). With one context in play that is
  | unambiguously this room. With more than one it may describe the cohort the
  | facilitator is not looking at, and a nav badge that might be counting another
  | room is worse than no badge, so it is suppressed instead. Adding a cohort
  | scope to that aggregate is a named backend follow-up, not something the
  | frontend can fake.
  */
  const badgeScopeUnambiguous = contexts.length <= 1;
  const expectedLevel = selectExpectedPace(data)?.resolvedLevel ?? null;
  const {
    snapshot: reviewLifecycle,
    status: reviewLifecycleStatus,
    refresh: refreshReviewLifecycle,
  } = useReviewLifecycle({
    enabled: status === "ready" && badgeScopeUnambiguous,
    expectedLevel,
    sourceGeneratedAt: data?.generated_at ?? null,
  });
  const reviewPending = reviewLifecycle?.pending ?? null;
  const reviewLifecycleForPace = useMemo(
    () =>
      reviewLifecycle
        ? {
            ...reviewLifecycle,
            levels: reviewLifecycle.levels.map((row) => ({
              ...row,
              lifecycle: lifecycleForLevel(row, expectedLevel),
            })),
          }
        : null,
    [reviewLifecycle, expectedLevel],
  );

  const value = useMemo(
    () => ({
      envelope: data,
      status,
      error,
      refetch,
      refreshSnapshot,
      facilitatorRoleId,
      onboardingRequired,
      roomLevel,
      setRoomLevel,
      requested,
      setRequested,
      contexts,
      period,
      setPeriod,
      periodOptions: options,
      filterSquads,
      namesConcealed,
      privacyEpoch,
      setNamesConcealed,
      reviewPending,
      reviewLifecycle: reviewLifecycleForPace,
      reviewLifecycleStatus,
      refreshReviewLifecycle,
      refreshReviewPending: refreshReviewLifecycle,
    }),
    [
      data,
      status,
      error,
      refetch,
      refreshSnapshot,
      facilitatorRoleId,
      onboardingRequired,
      roomLevel,
      setRoomLevel,
      requested,
      contexts,
      period,
      setPeriod,
      options,
      filterSquads,
      namesConcealed,
      privacyEpoch,
      setNamesConcealed,
      reviewPending,
      reviewLifecycleForPace,
      reviewLifecycleStatus,
      refreshReviewLifecycle,
    ],
  );

  return (
    <FacilitatorContext.Provider value={value}>
      {children}
    </FacilitatorContext.Provider>
  );
};

export default FacilitatorContextProvider;
